import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouterLegacy } from 'core/routerUtils';
import { withTranslation } from 'react-i18next';
import { createRoot } from 'react-dom/client';
import { AlertNotice, SimpleModal } from 'shared/components';
import { SeatingChartGrid, SeatingChartSelectionBar } from 'features/seating-chart';
import * as seatingChartActions from 'features/seating-chart/duck';
import { checkSerialNumberLength, checkSerialNumberStartDigit } from '../validations';
import { cloneDeep, concat, filter, find, isEmpty, sumBy } from 'lodash';

import './SeatingChartEquating.scss';

const generateRegMaterials = (seatsData, materialTypeId) => {
  let data = [];
  let emptyCell = {};
  let seats = cloneDeep(seatsData);
  seats?.map((seatsRow) =>
    seatsRow?.map((seat) => {
      if (seat) {
        if (isEmpty(data[seat.seatRow])) {
          data[seat.seatRow] = [];
        }
        emptyCell = {
          regId: seat.id,
          materialTypeId: materialTypeId,
          serialNumber: '',
        };

        seat.regMaterials = find(seat.regMaterials, {
          materialTypeId: materialTypeId,
        });

        data[seat.seatRow][seat.seatCol] = !isEmpty(seat.regMaterials)
          ? seat.regMaterials
          : emptyCell;
      }
    })
  );
  return data;
};

const removeSeatsError = (seatsData) => {
  seatsData?.map((rowData) => {
    rowData?.map((cell) => {
      if (cell) {
        delete cell.error;
      }
    });
  });
  return seatsData;
};

const getTotalBooklets = (ranges) => sumBy(ranges, 'count');

class SeatingChartEquating extends Component {
  constructor(props) {
    super(props);

    this.state = {
      discardModalOpen: false,
      clearAllModalOpen: false,
      assignMoreBookletsModalOpen: false,
      isDirty: false,
      seatsData: [],
      materialType: {
        name: '',
      },
      totalBooklets: 0,
      errors: [],
      isButtonDisabled: false,
    };

    this.handleSelectSeat = this.handleSelectSeat.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleBack = this.handleBack.bind(this);
    this.handleClearAll = this.handleClearAll.bind(this);
    this.closeDiscardModal = this.closeDiscardModal.bind(this);
    this.toggleClearAllModal = this.toggleClearAllModal.bind(this);
    this.toggleAssignMoreBookletsModal = this.toggleAssignMoreBookletsModal.bind(this);
    this.setDOM = this.setDOM.bind(this);
    this.removeSticky = this.removeSticky.bind(this);
    this.toggleStickySection = this.toggleStickySection.bind(this);
    this.seatingChart = React.createRef();
  }

  componentDidMount() {
    const { match, roomsRanges, roomTypeMaterialTypes } = this.props;

    const materialTypeId = +match.params.materialId;
    const materialType = find(roomTypeMaterialTypes, { id: materialTypeId });
    const seatsData = generateRegMaterials(this.props.seatsData, materialTypeId);

    this.setState((prevState) => ({
      materialType: isEmpty(materialType) ? prevState.materialType : materialType,
      seatsData: seatsData,
      totalBooklets: getTotalBooklets(filter(roomsRanges, { materialTypeId: materialTypeId })),
    }));

    window.addEventListener('scroll', this.toggleStickySection);
    this.setDOM();
  }

  componentWillUnmount() {
    this.removeSticky();
  }

  handleSelectSeat(i, j, serialNumber) {
    let seats = cloneDeep(this.state.seatsData);

    if (seats[i][j]) {
      seats[i][j].serialNumber = serialNumber;

      this.setState({
        seatsData: removeSeatsError(seats),
        isDirty: true,
        errors: [],
        isButtonDisabled: false,
      });
    }
    this.toggleStickySection();
  }

  handleClearAll() {
    let seats = cloneDeep(this.state.seatsData);

    seats?.map((seatsRow) =>
      seatsRow?.map((seat) => {
        if (!isEmpty(seat)) {
          seat.serialNumber = '';
        }
      })
    );

    this.setState({
      seatsData: removeSeatsError(seats),
      isDirty: true,
      errors: [],
    });
  }

  handleBlur() {
    const { seatsData } = this.state;
    const errors = concat(
      checkSerialNumberLength(seatsData),
      checkSerialNumberStartDigit(seatsData)
    );
    if (errors.length) {
      this.setState({
        seatsData: seatsData,
        errors: [errors[0]],
        isButtonDisabled: true,
      });
      this.toggleStickySection();
    }
  }

  async handleSave() {
    const { actions, match, room } = this.props;
    const { materialType, seatsData } = this.state;

    // let errors = [];
    const errors = concat(
      checkSerialNumberLength(seatsData),
      checkSerialNumberStartDigit(seatsData)
    );

    if (errors.length) {
      this.setState({
        seatsData: seatsData,
        errors: [errors[0]],
      });
      this.toggleStickySection();
    } else {
      await actions.assignMaterialsToRegs(room.id, {
        materialTypeId: +match.params.materialId,
        regMaterials: filter(
          seatsData.flat().filter((n) => n),
          (n) => n?.serialNumber
        ),
      });
      await actions.getRegSummaries(room.id);

      sessionStorage.setItem(
        'successSavedMessage',
        this.props.t('seatingChart.assign.successMessage', {
          materialName: materialType.name,
        })
      );
      this.props.history.goBack();
    }
  }

  handleBack() {
    if (this.state.isDirty) {
      this.setState({
        discardModalOpen: true,
      });
    } else {
      this.props.history.goBack();
    }
  }

  closeDiscardModal(goBack) {
    if (goBack) {
      this.props.history.goBack();
    }
    this.setState({
      discardModalOpen: false,
    });
  }

  toggleClearAllModal(clear) {
    if (clear) {
      this.handleClearAll();
    }
    this.setState((prevState) => ({
      clearAllModalOpen: !prevState.clearAllModalOpen,
    }));
  }

  toggleAssignMoreBookletsModal(assign) {
    if (assign) {
      this.handleSave();
    }
    this.setState((prevState) => ({
      assignMoreBookletsModalOpen: !prevState.assignMoreBookletsModalOpen,
    }));
  }

  setDOM() {
    const section = document.getElementsByClassName('sticky-element')[0];
    const newDiv = document.createElement('div');

    newDiv.className = 'equating-sticky';
    section.appendChild(newDiv);
    window.scrollTo(0, 0);

    this.stickyRoot = createRoot(document.getElementsByClassName('equating-sticky')[0]);
    this.stickyRoot.render(<div className="preview-sticky"></div>);
  }

  removeSticky() {
    const section = document.getElementsByClassName('sticky-element')[0];
    const child = document.getElementsByClassName('equating-sticky')[0];

    if (this.stickyRoot) {
      this.stickyRoot.unmount();
      this.stickyRoot = null;
    }

    if (child) {
      section.removeChild(child);
    }

    window.removeEventListener('scroll', this.toggleStickySection);
  }

  toggleStickySection() {
    const errorSection = document.getElementsByClassName('equating-movable-errors')[0];
    if (window.pageYOffset > 100 && errorSection && errorSection.hasChildNodes()) {
      const toAppendSection = document.getElementsByClassName('preview-sticky')[0];
      if (!toAppendSection.hasChildNodes()) {
        toAppendSection.appendChild(errorSection);
      }
    } else if (errorSection) {
      const toAppendSection = document.getElementsByClassName('equating-header')[0];
      toAppendSection.appendChild(errorSection);
    }
  }

  render() {
    const { room, match, roomsRanges } = this.props;

    const {
      discardModalOpen,
      clearAllModalOpen,
      assignMoreBookletsModalOpen,
      materialType,
      seatsData,
      errors,
      totalBooklets,
    } = this.state;

    let assignedSeats = 0;
    seatsData?.map((seatsRow) =>
      seatsRow?.map((seat) => {
        if (seat?.serialNumber) {
          assignedSeats++;
        }
      })
    );

    const ranges = filter(roomsRanges, {
      materialTypeId: +match.params.materialId,
    });

    return (
      <div className="seating-chart-equating-seats">
        <div className="container title-container equating-header mb-30">
          <h4 className="pt-3">
            {this.props.t('seatingChart.assign.title', {
              materialName: materialType.name,
            })}
          </h4>
          <p className="text-strong">{this.props.t('seatingChart.assign.subtitle')}</p>

          <div className="equating-movable-errors">
            {errors.map((error, idx) => (
              <AlertNotice message={this.props.t(error)} type="error" key={idx} />
            ))}
          </div>
        </div>

        <div className="bg-white-smoke">
          <div className="select-bar">
            <div className="container">
              <div className="assign-booklets">
                <div>{this.props.t('seatingChart.rangeLabel')}</div>
                <div>
                  {ranges.map((range, idx) => (
                    <div key={idx}>
                      {range.rangeBegin} - {range.rangeEnd} ({range.count})
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <button
                  className="btn-link"
                  disabled={!assignedSeats}
                  onClick={() => this.toggleClearAllModal(false)}>
                  {this.props.t('common.labels.clearAll')}
                </button>
              </div>
            </div>
          </div>

          <div className="container" ref={this.seatingChart}>
            <SeatingChartGrid
              rows={room.seatingRowCount}
              cols={room.seatingColumnCount}
              seatsData={seatsData}
              type="equating"
              handleSeatClick={this.handleSelectSeat}
              seatingChart={this.seatingChart}
              handleBlur={this.handleBlur}
            />
          </div>
        </div>

        <SeatingChartSelectionBar
          count={assignedSeats}
          total={totalBooklets}
          selectedSeatsLabel="seatingChart.usedOf"
          handleSave={() =>
            assignedSeats > totalBooklets
              ? this.toggleAssignMoreBookletsModal(false)
              : this.handleSave()
          }
          handleBack={this.handleBack}
          isButtonDisabled={this.state.isButtonDisabled}
        />

        <SimpleModal
          isOpen={discardModalOpen}
          onCancel={() => this.closeDiscardModal(false)}
          onConfirm={() => this.closeDiscardModal(true)}
          confirmLabel={this.props.t('common.labels.yes')}
          cancelLabel={this.props.t('common.labels.no')}
          headline={this.props.t('common.backModal.title')}
          message={this.props.t('common.backModal.discardPageMessage')}
          buttonsReverseOrder
        />

        <SimpleModal
          isOpen={clearAllModalOpen}
          onCancel={() => this.toggleClearAllModal(false)}
          onConfirm={() => this.toggleClearAllModal(true)}
          confirmLabel={this.props.t('common.labels.clearAll')}
          cancelLabel={this.props.t('common.labels.no')}
          headline={this.props.t('seatingChart.clearAllModalTitle')}
          message={this.props.t('seatingChart.clearAllModalMessage')}
        />

        <SimpleModal
          isOpen={assignMoreBookletsModalOpen}
          onCancel={() => this.toggleAssignMoreBookletsModal(false)}
          onConfirm={() => this.toggleAssignMoreBookletsModal(true)}
          confirmLabel={this.props.t('common.labels.continue')}
          cancelLabel={this.props.t('common.labels.back')}
          headline={this.props.t('seatingChart.assign.assignMoreBookletsModalTitle')}
          message={this.props.t('seatingChart.assign.assignMoreBookletsModalMessage')}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    room: state.roomOptions.room || {},
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...seatingChartActions,
      },
      dispatch
    ),
  };
}

export default withTranslation()(
  withRouterLegacy(connect(mapStateToProps, mapDispatchToProps)(SeatingChartEquating))
);

SeatingChartEquating.propTypes = {
  t: PropTypes.func.isRequired,
};
